import { useState } from 'react';
import { useT } from '@/store';
import { useMaterials } from '@/hooks/useDirectory';
import { useDeleteQuarryPrice, useQuarryPrices, useSaveQuarryPrice } from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { parseDecimalInput } from '@/lib/format';

/**
 * Kainodara — one €/t price per material per quarry.
 *
 * This is the table the whole margin column in Analytics rests on: an order
 * whose (quarry, material) pair has no row here contributes no revenue
 * figure, which is why Analytics reports "unpriced orders" separately rather
 * than quietly treating them as zero. The material dropdown is fed from the
 * tenant's own catalogue so a typo cannot create a price that no order will
 * ever match.
 *
 * BOTH SIDES OF THE ROW SINCE V-37. The second box is what the QUARRY
 * charges, against the first, which is what the CLIENT is charged. For a
 * carrier who buys aggregate and resells it delivered, that is the largest
 * cost line in his business and "net margin" had never subtracted a cent of
 * it — a load sold at 10,00 out of a quarry charging 6,00 read as three
 * times the profit it made.
 *
 * IT IS OPTIONAL AND STAYS OPTIONAL. A carrier who only hauls does not buy
 * the material: his client buys from the quarry and this box is empty
 * forever, which is the correct state and changes no figure anywhere. That
 * is why there is no setup-checklist step for it — a line half the customers
 * can never tick would tell them their installation is unfinished for as
 * long as they own the product.
 */
export function QuarryPriceList({ quarryId }) {
  const t = useT();
  const { data: prices = [], isLoading } = useQuarryPrices(quarryId);
  const { data: materials = [] } = useMaterials();
  const savePrice = useSaveQuarryPrice();
  const [formError, setFormError] = useState(null);
  const deletePrice = useDeleteQuarryPrice();

  const [materialName, setMaterialName] = useState('');
  const [pricePerTon, setPricePerTon] = useState('');
  const [costPerTon, setCostPerTon] = useState('');

  const add = () => {
    const value = parseDecimalInput(pricePerTon);
    // V-47, the third of them: the price list is checklist step three, and
    // it refused in silence too.
    if (!materialName) return setFormError('data.errMaterialName');
    if (Number.isNaN(value)) return setFormError('data.errPricePerTon');
    setFormError(null);
    const cost = parseDecimalInput(costPerTon);
    savePrice.mutate(
      {
        quarryId, material_name: materialName, price_per_ton: value,
        // Absent when the box is empty, never 0. Number('') is 0 in
        // JavaScript, and a 0 here says the quarry gives the stone away —
        // which would count as a fully-costed load in every coverage figure
        // on the Analytics tab. The backend refuses it; this makes sure the
        // empty box never asks.
        ...(Number.isFinite(cost) ? { cost_per_ton: cost } : null),
      },
      {
        onSuccess: () => {
          setMaterialName('');
          setPricePerTon('');
          setCostPerTon('');
        },
      },
    );
  };

  /* Blank clears the cost — a real instruction, and the way a carrier undoes
     a number typed onto the wrong material. null, not 0: see add() above. */
  const saveCost = (price) => (event) => {
    // V-339. The comma fold that used to be spelled out here is now
    // parseDecimalInput()'s job, shared with the price box beside it — this
    // was the only one of the two that had it, which is exactly how the
    // other one went four percent light without anybody noticing.
    const text = event.target.value.trim();
    if (text === '') {
      if (price.cost_per_ton == null) return;
      savePrice.mutate({ quarryId, priceId: price.price_id, cost_per_ton: null });
      return;
    }
    const value = parseDecimalInput(text);
    if (!Number.isFinite(value) || value === price.cost_per_ton) return;
    savePrice.mutate({ quarryId, priceId: price.price_id, cost_per_ton: value });
  };

  return (
    <div className="space-y-2">
      <h4 className="panel-title !mb-1">{t('data.pricesHeading')}</h4>

      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : prices.length === 0 ? (
        <EmptyState>{t('data.noPricesYet')}</EmptyState>
      ) : (
        <ul className="space-y-1">
          {prices.map((price) => (
            <li key={price.price_id} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="min-w-0 flex-1 truncate">{price.material_name}</span>
              <input
                className="field !w-24 !py-1 !text-[11px] text-right"
                defaultValue={price.price_per_ton}
                inputMode="decimal"
                // Committed on blur, not per keystroke: a price is a
                // deliberate edit, and saving mid-typing would briefly store
                // "1" on the way to "12.50".
                onBlur={(e) => {
                  const value = parseDecimalInput(e.target.value);
                  if (Number.isNaN(value) || value === price.price_per_ton) return;
                  savePrice.mutate({ quarryId, priceId: price.price_id, price_per_ton: value });
                }}
              />
              <span className="text-dim">€/t</span>
              {/* What the quarry charges for the same tonne. Empty is the
                  ordinary state for a carrier who only hauls, and the hint
                  under the list is what stops an empty column reading as a
                  missing figure rather than an absent cost. */}
              <input
                className="field !w-24 !py-1 !text-[11px] text-right"
                defaultValue={price.cost_per_ton ?? ''}
                inputMode="decimal"
                placeholder={t('data.costPh')}
                aria-label={t('data.costLabel')}
                onBlur={saveCost(price)}
              />
              <span className="text-dim">{t('data.costUnit')}</span>
              <ConfirmButton
                label="✕"
                confirmLabel={t('common.confirmDelete')}
                onConfirm={() => deletePrice.mutate({ quarryId, priceId: price.price_id })}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <select className="field !w-auto flex-1 !py-1 !text-[11px]" value={materialName} onChange={(e) => setMaterialName(e.target.value)}>
          <option value="">{t('data.pickMaterial')}</option>
          {materials.map((m) => (
            <option key={m.material_id} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>
        <input
          className="field !w-24 !py-1 !text-[11px]"
          value={pricePerTon}
          onChange={(e) => setPricePerTon(e.target.value)}
          placeholder="€/t"
          inputMode="decimal"
        />
        <input
          className="field !w-24 !py-1 !text-[11px]"
          value={costPerTon}
          onChange={(e) => setCostPerTon(e.target.value)}
          placeholder={t('data.costPh')}
          aria-label={t('data.costLabel')}
          inputMode="decimal"
        />
        <button type="button" className="btn" onClick={add} disabled={savePrice.isPending}>
          {t('common.addBtn')}
        </button>
      </div>

      <p className="text-[10.5px] leading-snug text-dim">{t('data.costHint')}</p>

      {formError ? <ErrorState error={new Error(t(formError))} /> : null}
      {savePrice.isError ? <ErrorState error={savePrice.error} /> : null}
      {deletePrice.isError ? <ErrorState error={deletePrice.error} /> : null}
    </div>
  );
}
